import { ChangeDetectionStrategy, Component, DestroyRef, Inject, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ChatMessage, ChatRelationEvent, memberName } from 'src/app/models/chat';
import { AuthService } from 'src/app/services/auth.service';
import { ChatService } from 'src/app/services/chat.service';
import { I18nService } from 'src/app/services/i18n.service';
import { DialogContentLogin } from '../header-sticky/login-dialog/dialog-content-login';

// Icône « discussions » du header, à côté des notifications : badge des messages non lus
// + demandes d'ami reçues, et alertes en temps réel quand la page des messages n'est pas ouverte.
@Component({
  selector: 'app-chat-button',
  templateUrl: './chat-button.component.html',
  styleUrls: ['./chat-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ChatButtonComponent implements OnInit {

  total = 0;
  private destroyRef = inject(DestroyRef);

  constructor(
    private chatService: ChatService,
    private authService: AuthService,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private i18n: I18nService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.chatService.unreadTotal$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(total => this.total = total);
    this.chatService.relationEvents$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => this.onRelationEvent(event));
    this.chatService.messages$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(message => this.onMessage(message));
  }

  get badge(): string {
    return this.total > 9 ? '9+' : String(this.total);
  }

  open(): void {
    if (!this.authService.currentUserValue) {
      this.dialog.open(DialogContentLogin, { width: '550px', data: {} });
      return;
    }
    this.router.navigate(['/messages']);
  }

  private onRelationEvent(event: ChatRelationEvent): void {
    if (!event.user || this.onMessagesPage()) {
      return;
    }
    if (event.type === 'request') {
      this.notify(this.i18n.t('chat.requestReceived', { pseudo: memberName(event.user) }), ['/messages'], { tab: 'requests' });
    } else if (event.type === 'accepted') {
      this.notify(this.i18n.t('chat.requestAccepted', { pseudo: memberName(event.user) }), ['/messages', event.user._id]);
    }
  }

  private onMessage(message: ChatMessage): void {
    const incoming = String(message.to) === String(this.chatService.myId);
    if (!incoming || this.onMessagesPage()) {
      return;
    }
    const friend = this.chatService.relationsValue?.friends.find(f => String(f.user._id) === String(message.from));
    const pseudo = memberName(friend?.user);
    this.notify(this.i18n.t('chat.newMessageFrom', { pseudo }), ['/messages', String(message.from)]);
  }

  private notify(text: string, commands: string[], queryParams?: { [key: string]: string }): void {
    this.snackBar.open(text, this.i18n.t('chat.view'), { duration: 6000, verticalPosition: 'top' })
      .onAction().subscribe(() => this.router.navigate(commands, { queryParams }));
  }

  private onMessagesPage(): boolean {
    return this.router.url.startsWith('/messages');
  }
}
